import React, { useState, useEffect } from 'react';
import { User, Mail, Building2, MapPin, Globe, Phone, Save, Loader2 } from 'lucide-react';
import { useAuthContext } from '../../auth/data/context/AuthContext';
import { AdminProfile } from '../../auth/data/api/AuthClient';
import Alert, { AlertType } from '../../../shared/components/Alert';
import '../../profile/styles/ProfilePage.css';
import '../../profile/styles/WhiteLabelPage.css';
import './ProfileEditForm.css';

const ORG_TYPES = ['Corporate', 'Startup', 'Enterprise', 'SME', 'Government', 'Non-Profit'];

const ProfileEditForm: React.FC = () => {
  const { state: { user, isSaving }, updateProfile } = useAuthContext();
  const [formData, setFormData] = useState<Partial<AdminProfile>>({});
  const [errorMsg, setErrorMsg] = useState('');

  const [alertConfig, setAlertConfig] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: AlertType;
  }>({
    show: false,
    title: '',
    message: '',
    type: 'success'
  });

  const closeAlert = () => setAlertConfig(prev => ({ ...prev, show: false }));

  useEffect(() => {
    if (user) {
      setFormData({ ...user });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setErrorMsg('');
    const saveData: Partial<AdminProfile> = {
      company_name: formData.company_name,
      organization_type: formData.organization_type,
      headquarters_location: formData.headquarters_location,
      website_url: formData.website_url,
      contact_email_address: formData.contact_email_address,
      contact_number: formData.contact_number,
    };

    const result = await updateProfile(saveData);

    if (result.success) {
      setAlertConfig({
        show: true,
        title: 'Profile Updated',
        message: 'Your profile information has been saved successfully.',
        type: 'success'
      });
    } else {
      setErrorMsg(result.error || 'Failed to save profile. Please try again.');
    }
  };

  return (
    <div className="pe-page">
      {/* Scrollable content area */}
      <div className="pe-main">
        {/* Header — matches WhiteLabelPage layout */}
        <div className="wl-header">
          <div className="wl-header-info">
            <div className="wl-title-row">
              <h1>Account Information</h1>
              <span className="wl-header-desc">Update your company details and contact information.</span>
            </div>
          </div>
        </div>

        {errorMsg && <div className="pf-error-message" style={{ marginBottom: '16px' }}>{errorMsg}</div>}

        <div className="pf-info-sections">
          <div className="pf-section-card">
            <div className="pf-section-header">
              <Building2 size={18} />
              <h3>Company Information</h3>
            </div>
            <div className="pf-field-grid">
              <Field label="Company Name" name="company_name" icon={<Building2 size={14} />} formData={formData} onChange={handleInputChange} />
              <Field label="Organization Type" name="organization_type" icon={<Building2 size={14} />} formData={formData} onChange={handleInputChange} options={ORG_TYPES} />
              <Field label="Headquarters" name="headquarters_location" icon={<MapPin size={14} />} formData={formData} onChange={handleInputChange} />
              <Field label="Website URL" name="website_url" icon={<Globe size={14} />} formData={formData} onChange={handleInputChange} />
            </div>
          </div>

          <div className="pf-section-card" style={{ marginTop: '24px' }}>
            <div className="pf-section-header">
              <User size={18} />
              <h3>Contact Information</h3>
            </div>
            <div className="pf-field-grid">
              <Field label="Account Email (Login)" name="email_address" icon={<Mail size={14} />} formData={formData} onChange={handleInputChange} disabled />
              <Field label="Contact Email" name="contact_email_address" icon={<Mail size={14} />} formData={formData} onChange={handleInputChange} />
              <Field label="Contact Number" name="contact_number" icon={<Phone size={14} />} formData={formData} onChange={handleInputChange} />
            </div>
          </div>
        </div>
      </div>

      {/* Pinned footer */}
      <div className="pe-footer">
        <button className="wl-btn-secondary" onClick={() => setFormData(user ? { ...user } : {})}>
          Cancel
        </button>
        <button className="wl-btn-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 size={16} className="pf-spin" /> : <Save size={16} />}
          Save Changes
        </button>
      </div>

      <Alert
        show={alertConfig.show}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={closeAlert}
        primaryButtonText="Got it"
        onPrimaryClick={closeAlert}
      />
    </div>
  );
};

export default ProfileEditForm;

interface FieldProps {
  label: string;
  name: keyof AdminProfile;
  icon: React.ReactNode;
  formData: Partial<AdminProfile>;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  disabled?: boolean;
  type?: string;
  options?: string[];
}

const Field: React.FC<FieldProps> = ({
  label, name, icon, formData, onChange, disabled = false, type = 'text', options
}) => (
  <div className="pf-field-item">
    <label className="pf-field-label">
      {icon}
      {label}
    </label>
    {options ? (
      <select
        name={name}
        value={(formData[name] as string) || ''}
        onChange={onChange}
        className="pf-input-box"
        disabled={disabled}
      >
        <option value="">Select type</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    ) : (
      <input
        type={type}
        name={name}
        value={(formData[name] as string) || ''}
        onChange={onChange}
        className="pf-input-box"
        disabled={disabled}
      />
    )}
  </div>
);
